import { memo, createRef, useCallback, useState, useEffect, useMemo } from 'react';

import CardForm from 'components/CardForm';
import ProductsListModal from 'components/ProductsListModal';
import SelectedProductItemCardForm from 'components/SelectedProductItemCardForm';
import { useFormikContext } from 'formik';
import useDebounce from 'hooks/useDebounce';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { orderBumpService } from 'services';
import { ButtonOutline, IModal, If } from 'shared';
import AlertDialog, { IAlertDialog } from 'shared/AlertDialog';
import { Button } from 'shared/Houston';
import { SelectProductItemSkeleton } from 'shared/SelectProductItemSkeleton';
import { orderBumpFormActions } from 'store/features';
import { selectOrderBumpForm } from 'store/selectors/orderBumpForm/index';
import { enOrderBumpStatus, enProductType, enSearchParams } from 'types/enums';
import { IProduct, IOrderBumpForm, IOrderBumpFormItem, IOrderBumpProduct, ISelectedProduct } from 'types/interfaces';
import { renderToastMessageApi } from 'utils/toastMessage';

import Add from '@eduzz/houston-icons/Add';
import TrashOutline from '@eduzz/houston-icons/TrashOutline';

import DescriptionBumpItem from './DescriptionBumpItem/DescriptionBumpItem';

const BumpsProductsSelection = () => {
  const dispatch = useDispatch();
  const refModal = createRef<IModal>();
  const refAlertDialog = createRef<IAlertDialog>();
  const formContext = useFormikContext<IOrderBumpForm<IOrderBumpFormItem>>();
  const { mainProduct, orderBumpsProducts, bumpItemsLimit = 1 } = useSelector(selectOrderBumpForm);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [bumpsAvailableToSelect, setBumpsAvailableToSelect] = useState(bumpItemsLimit);
  const [bumps, setBumps] = useState<Omit<IOrderBumpProduct, 'children'>[]>([]);

  const ownerContentId = mainProduct?.contentId;
  const term = useDebounce(searchParams.get(enSearchParams.termProductBumps));

  const { data, fetchNextPage, isSuccess, isFetchingNextPage, isFetching, isLoading } = useInfiniteQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: !!ownerContentId,
    queryKey: ['bumpsProductList', term, ownerContentId],
    queryFn(config) {
      return orderBumpService.getValidBumpsProducts(ownerContentId, { ...config, term });
    },
    getNextPageParam({ lastPage, page }) {
      if (lastPage - page <= 0) {
        return;
      }

      return ++page;
    },
    onSuccess(data) {
      const bumpItemsLimit = data.pages[0].bumpItemsLimit ?? 0;
      dispatch(orderBumpFormActions.setBumpItemsLimit(bumpItemsLimit));
    }
  });

  const productsList = useMemo(() => {
    return data?.pages.flatMap(({ data }) => data);
  }, [data?.pages]);

  const handleCloseModal = () => {
    refModal.current?.closeModal();
    queryClient.cancelQueries({ queryKey: ['bumpsProductList', term, ownerContentId] });
  };

  const mapBumpItemsToPayload = useCallback((products: IOrderBumpProduct[]) => {
    return products.map((product, index) => ({
      id: product.id ?? 0,
      contentId: product.contentId,
      offerId: product.offerId,
      order: index,
      status: product.status || enOrderBumpStatus.ACTIVE,
      description: product.description || ''
    }));
  }, []);

  const addBumpsProduct = useCallback(
    (products: IProduct[]) => {
      const currentBumps = [...bumps];

      currentBumps.forEach(bump => {
        const productIndex = products.findIndex(product => product.contentId === bump.contentId);

        if (productIndex === -1) {
          return;
        }

        products.splice(productIndex, 1);
      });

      refModal.current?.closeModal();

      const selectedBumps = currentBumps.concat(products);
      dispatch(orderBumpFormActions.setBumpItems(selectedBumps));
      formContext.setFieldValue('items', mapBumpItemsToPayload(selectedBumps));
    },
    [refModal, bumps, formContext, mapBumpItemsToPayload, dispatch]
  );

  const removeBump = useCallback(
    (id: number) => () => {
      const removeBumpIndex = bumps.findIndex(bump => bump.contentId === id);

      if (removeBumpIndex === -1) {
        return;
      }

      const currentBumps = [...bumps];
      currentBumps.splice(removeBumpIndex, 1);
      dispatch(orderBumpFormActions.setBumpItems(currentBumps));
      dispatch(orderBumpFormActions.addRemovedItems([id]));
      formContext.setFieldValue('items', mapBumpItemsToPayload(currentBumps));
    },
    [bumps, formContext, mapBumpItemsToPayload, dispatch]
  );

  const getProductName = useCallback(
    (id: number) => {
      const product = bumps.find(product => product.contentId === id);
      return product?.title || '';
    },
    [bumps]
  );

  const handleClickRemoveButton = useCallback(
    (id: number) => () => {
      refAlertDialog.current?.showAlertDialog({
        title: 'Remover Order Bump',
        text: `Você está removendo o Order Bump: ${getProductName(id)}`,
        confirmActionTextButton: 'Remover',
        onConfirm: removeBump(id)
      });
    },
    [refAlertDialog, removeBump, getProductName]
  );

  const isValidInfoPhysicalBumpsCombination = useCallback(
    (productId: number, selectedProductsIdsOnList: number[], allProducts: IProduct[]) => {
      const selectedProduct = allProducts.find(product => product.contentId === productId);

      if (!selectedProduct) {
        console.error('Selected product was not found on productList object:', productId);
        return true;
      }

      if (mainProduct?.type === enProductType.INFO && selectedProduct.type === enProductType.PHYSICAL) {
        const selectedProductsOnList = allProducts.filter(product =>
          selectedProductsIdsOnList.some(selectedProductId => selectedProductId === product.contentId)
        );

        return !selectedProductsOnList?.some(product => product.type === enProductType.PHYSICAL);
      }

      return true;
    },
    [mainProduct]
  );

  const validateInfoPhysicalBumpsCombination = useCallback(
    (productId: number, selectedProductsOnList: ISelectedProduct[], allProducts: IProduct[]) => {
      const selectedProductsIdsOnList = selectedProductsOnList.map(product => product.contentId);
      const isValid = isValidInfoPhysicalBumpsCombination(productId, selectedProductsIdsOnList, allProducts);

      if (!isValid) {
        renderToastMessageApi(400, 'Só é possível selecionar um produto físico');
      }

      return isValid;
    },
    [isValidInfoPhysicalBumpsCombination]
  );

  const onBlurDescription = (contentId: number, description: string) => {
    const currentBumps = [...bumps];

    const findIndexOfBumpItem = currentBumps.findIndex(bumpItem => bumpItem.contentId === contentId);
    if (findIndexOfBumpItem < 0) {
      return;
    }

    currentBumps[findIndexOfBumpItem] = {
      ...currentBumps[findIndexOfBumpItem],
      description
    };

    dispatch(orderBumpFormActions.setBumpItems(currentBumps));
    formContext.setFieldValue('items', mapBumpItemsToPayload(currentBumps));
  };

  useEffect(() => {
    setBumps(orderBumpsProducts as IProduct[]);
  }, [orderBumpsProducts]);

  useEffect(() => {
    const bumpsSelected = bumpItemsLimit - bumps.length;
    setBumpsAvailableToSelect(bumpsSelected);
  }, [bumps, bumpItemsLimit]);

  useEffect(() => {
    const subtitle = bumpItemsLimit > 1 ? `Selecione até ${bumpsAvailableToSelect} produtos` : 'Selecione um produto';

    if (bumpItemsLimit > 1 && mainProduct?.type === enProductType.INFO) {
      setModalSubtitle(`${subtitle}  (é possível incluir 1 produto físico)`);
      return;
    }

    setModalSubtitle(subtitle);
  }, [bumpItemsLimit, bumpsAvailableToSelect, mainProduct]);

  return (
    <>
      <CardForm
        title='Produto Order Bump'
        subtitle={
          <span>
            {bumpItemsLimit > 1 ? (
              <span>Selecione até {bumpItemsLimit} produtos que serão ofertados como Order Bump.</span>
            ) : (
              'Selecione o produto que será ofertado como Order Bump'
            )}
          </span>
        }
        action={
          <Button
            disabled={bumps.length >= bumpItemsLimit}
            startIcon={<Add />}
            onClick={() => refModal.current?.openModal()}
          >
            Adicionar Order Bump
          </Button>
        }
      />

      <If condition={bumps.length === 0}>
        <CardForm>
          <SelectProductItemSkeleton loading={isLoading} />
        </CardForm>
      </If>

      {bumps.map((bump, index) => (
        <SelectedProductItemCardForm
          key={`selected-bump-${bump.contentId}`}
          action={
            <ButtonOutline startIcon={<TrashOutline />} onClick={handleClickRemoveButton(bump.contentId)}>
              Remover
            </ButtonOutline>
          }
          product={bump}
          position={{
            order: index + 1,
            label: 'Order Bump'
          }}
          footer={
            <DescriptionBumpItem
              name={`bump-description-${bump.contentId}`}
              contentId={bump.contentId}
              onBlur={onBlurDescription}
              value={bump?.description}
            />
          }
        />
      ))}

      <ProductsListModal
        key={bumps.length}
        title='Selecionar Order Bump'
        subtitle={modalSubtitle}
        products={productsList ?? []}
        disabledSelectionProducts={bumps.map(bump => ({ contentId: bump.contentId, offerId: bump.offerId }))}
        closeList={handleCloseModal}
        addList={addBumpsProduct}
        ref={refModal}
        maxSelectedProducts={bumpItemsLimit}
        multipleSelection
        isInitialized={isSuccess}
        isEmptyProductList={isSuccess && productsList?.length === 0}
        customValidationOnSelect={validateInfoPhysicalBumpsCombination}
        onInView={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetchingByTerm={!isFetchingNextPage && !!term && isFetching}
        totalProducts={data?.pages[0].total}
        termSearch={enSearchParams.termProductBumps}
      />

      <AlertDialog ref={refAlertDialog} />
    </>
  );
};

export default memo(BumpsProductsSelection);
